import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import React, { useMemo } from "react";
import { Box, Button, Typography, Link } from "@mui/material";
import clsx from "clsx";
import ImageCarousel from "../../components/DashboardWidgets/ImageCarousel/ImageCarousel";
import { useTheme } from "@mui/material/styles";
import makeStyles from "@mui/styles/makeStyles";
import { Responsive } from "react-grid-layout";
import { withSize } from "react-sizeme";

const withSizeHOC = withSize({ refreshRate: 225, refreshMode: "debounce" });

const ResponsiveGridLayout = withSizeHOC(props => {
  return <Responsive {...props} width={props.size.width} />;
});

const useStyles = makeStyles(theme => ({
  "@global": {
    ".react-grid-item.react-grid-placeholder": {
      backgroundColor: theme.palette.action.hover
    },
    ".react-grid-item": {
      "& > .react-resizable-handle": {
        display: ({ editWidgetMode }) => (editWidgetMode ? "block" : "none"),
        backgroundImage: "none",
        "&:after": {
          height: 10,
          width: 10,
          right: 7,
          bottom: 7
        }
      },
      "&.react-draggable": {
        "&:hover": {
          cursor: "pointer"
        },
        "&.react-draggable-dragging": {
          boxShadow: "0px 0px 48px rgba(0, 0, 0, 0.2)",
          transition: "box-shadow 0.3s ease-in-out",
          "&:hover": {
            cursor: "grab"
          }
        }
      }
    }
  },
  dashboardContainerEditMode: {
    zIndex: 1202,
    position: "relative",
    maxHeight: `calc(100vh - ${theme.sizes.topNavBarHeight}px)`,
    overflowY: "scroll",
    "&:before": {
      content: "''",
      position: "fixed",
      zIndex: 1,
      height: theme.sizes.topNavBarHeight,
      width: "100%",
      left: 0,
      boxShadow: "0px 7px 10px -10px rgba(0,0,0,0.3)",
      top: 0
    },
    "& .react-grid-item > div": {
      border: `solid 4px ${theme.palette.primary.main}`
    }
  },
  root: {
    backgroundColor: theme.palette.body,
    width: "100%"
  },
  grid: {
    padding: theme.spacing(4, 1)
  },
  container: {
    display: "block",
    [theme.breakpoints.down("md")]: {
      display: "flex"
    }
  },
  editWidgetModeOverlay: {
    backgroundColor: theme.palette.secondary[50],
    opacity: 0.7,
    zIndex: 1201,
    position: "fixed",
    top: 0,
    bottom: 0,
    left: 0,
    right: 0
  },
  addWidgetLink: {
    marginLeft: 2,
    marginBottom: 3
  }
}));

const DashboardBanner = ({ showBanner, bannerImages, editWidgetMode }) => {
  return showBanner ? (
    <ImageCarousel
      bannerImages={bannerImages}
      imgStyle={editWidgetMode ? { filter: "opacity(0.3)" } : null}
    />
  ) : null;
};

const Dashboard = ({
  children,
  bannerImages,
  noBanner,
  widgetLayout,
  onOpenAddWidgetModal,
  onLayoutChange,
  onSaveLayout,
  widgetRowHeight,
  widgetMargin,
  customizable,
  editWidgetMode,
  onEditWidgetModeChange
}) => {
  const classes = useStyles({ editWidgetMode });
  const theme = useTheme();

  const { sm, md, lg } = theme.widgetLayoutBreakpoints;

  function handleLayoutChange(newLayout) {
    onLayoutChange(newLayout);
  }

  function handleSaveLayout() {
    onEditWidgetModeChange(false);
    onSaveLayout();
  }

  function customizeWidgets() {
    if (editWidgetMode) {
      return (
        <Box
          display="flex"
          justifyContent="center"
          mt={5}
          mb={2}
          mx={3}
          alignItems="center"
        >
          <Typography variant="h6">
            Drag each widget below to rearrange or{" "}
            <Link
              className={classes.addWidgetLink}
              component="button"
              onClick={() => onOpenAddWidgetModal()}
            >
              Add a Widget
            </Link>
          </Typography>
          <Box ml={5}>
            <Button
              color="primary"
              variant="contained"
              onClick={() => handleSaveLayout()}
            >
              Done Editing
            </Button>
          </Box>
        </Box>
      );
    }
    return (
      <Box display="flex" justifyContent="flex-end" mt={3} mr={0.5}>
        <Button color="primary" onClick={() => onEditWidgetModeChange(true)}>
          Customize Widgets
        </Button>
      </Box>
    );
  }
  const dashboardChildren = useMemo(() => children, [
    children.length,
    editWidgetMode
  ]);

  return (
    <Box className={classes.root}>
      {editWidgetMode && <div className={classes.editWidgetModeOverlay} />}
      <div
        className={clsx({
          [classes.dashboardContainerEditMode]: editWidgetMode
        })}
      >
        <DashboardBanner
          showBanner={!noBanner}
          bannerImages={bannerImages}
          editWidgetMode={editWidgetMode}
        />
        {customizable && customizeWidgets()}

        <ResponsiveGridLayout
          className="layout"
          layouts={widgetLayout}
          breakpoints={{ sm, md, lg }}
          cols={{ lg: 12, md: 8, sm: 4 }}
          measureBeforeMount={true}
          compactType="vertical"
          rowHeight={widgetRowHeight}
          margin={[widgetMargin, widgetMargin]}
          onResizeStop={handleLayoutChange}
          onDragStop={handleLayoutChange}
          isDraggable={editWidgetMode}
          isResizable={editWidgetMode}
          draggableCancel=".prevent-grid-layout-drag"
        >
          {dashboardChildren}
        </ResponsiveGridLayout>
      </div>
    </Box>
  );
};

export default Dashboard;
